// Exam & Exam Schedule API service

import { apiRequest } from './api';

// ── Types ───────────────────────────────────────────────────

export type ExamType = 'periodic' | 'midterm' | 'final' | 'supplementary';

export type ExamApiRecord = {
    id: string;
    organization_id: string;
    school_id: string;
    academic_session_id: string;
    name: string;
    exam_type: ExamType;
    start_date: string;
    end_date: string;
    is_active: boolean;
    metadata: Record<string, unknown> | null;
    created_at: string;
    updated_at: string;
    school?: { id: string; name: string };
    academic_session?: { id: string; name: string };
    schedules_count?: number;
};

export type ExamPayload = {
    school_id: string;
    academic_session_id: string;
    name: string;
    exam_type: ExamType;
    start_date: string;
    end_date: string;
    is_active?: boolean;
    metadata?: Record<string, unknown> | null;
};

export type ExamListResponse = {
    success: boolean;
    message: string;
    data: ExamApiRecord[];
    meta: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
    };
};

export type ExamScheduleApiRecord = {
    id: string;
    organization_id: string;
    exam_id: string;
    class_id: string;
    section_id: string | null;
    school_subject_id: string;
    exam_date: string;
    start_time: string;
    end_time: string;
    room_number: string | null;
    max_marks: string;
    pass_marks: string;
    invigilator_id: string | null;
    created_at: string;
    updated_at: string;
    school_class?: { id: string; name: string; standard: number };
    section?: { id: string; name: string } | null;
    subject?: { id: string; board_subject_id: number; board_subject_name: string };
    invigilator?: { id: string; first_name: string; last_name: string } | null;
};

export type SchedulePayload = {
    school_id: string;
    class_id: string;
    section_id?: string | null;
    school_subject_id: string;
    exam_date: string;
    start_time: string;
    end_time: string;
    room_number?: string | null;
    max_marks?: number;
    pass_marks?: number;
    invigilator_id?: string | null;
};

export type ScheduleListResponse = {
    success: boolean;
    message: string;
    data: ExamScheduleApiRecord[];
    meta: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
    };
};

export type AcademicSessionRecord = {
    id: string;
    organization_id: string;
    name: string;
    start_date: string;
    end_date: string;
    is_current: boolean;
    created_at: string;
    updated_at: string;
};

// ── Exam API Functions ──────────────────────────────────────

export async function fetchExams(params?: {
    page?: number;
    perPage?: number;
    search?: string;
    academicSessionId?: string;
    isActive?: boolean;
}): Promise<ExamListResponse> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.perPage) query.set('per_page', String(params.perPage));
    if (params?.search) query.set('search', params.search);
    if (params?.academicSessionId) query.set('academic_session_id', params.academicSessionId);
    if (params?.isActive !== undefined) query.set('is_active', params.isActive ? '1' : '0');

    const qs = query.toString();
    const res = await apiRequest<ExamApiRecord[]>(`/api/exams${qs ? `?${qs}` : ''}`);

    return {
        success: res.success,
        message: res.message,
        data: res.data,
        meta: (res as unknown as ExamListResponse).meta,
    };
}

export async function createExam(payload: ExamPayload): Promise<ExamApiRecord> {
    const res = await apiRequest<ExamApiRecord>('/api/exams', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
    return res.data;
}

export async function updateExam(id: string, payload: ExamPayload): Promise<ExamApiRecord> {
    const res = await apiRequest<ExamApiRecord>(`/api/exams/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
    });
    return res.data;
}

export async function deleteExam(id: string): Promise<void> {
    await apiRequest(`/api/exams/${id}`, { method: 'DELETE' });
}

// ── Schedule API Functions ──────────────────────────────────

export async function fetchExamSchedules(examId: string, params?: {
    page?: number;
    perPage?: number;
}): Promise<ScheduleListResponse> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.perPage) query.set('per_page', String(params.perPage));

    const qs = query.toString();
    const res = await apiRequest<ExamScheduleApiRecord[]>(
        `/api/exams/${examId}/schedules${qs ? `?${qs}` : ''}`
    );

    return {
        success: res.success,
        message: res.message,
        data: res.data,
        meta: (res as unknown as ScheduleListResponse).meta,
    };
}

export async function createExamSchedule(
    examId: string,
    payload: SchedulePayload
): Promise<ExamScheduleApiRecord> {
    const res = await apiRequest<ExamScheduleApiRecord>(
        `/api/exams/${examId}/schedules`,
        { method: 'POST', body: JSON.stringify(payload) }
    );
    return res.data;
}

export async function updateExamSchedule(
    examId: string,
    scheduleId: string,
    payload: SchedulePayload
): Promise<ExamScheduleApiRecord> {
    const res = await apiRequest<ExamScheduleApiRecord>(
        `/api/exams/${examId}/schedules/${scheduleId}`,
        { method: 'PUT', body: JSON.stringify(payload) }
    );
    return res.data;
}

export async function deleteExamSchedule(examId: string, scheduleId: string): Promise<void> {
    await apiRequest(`/api/exams/${examId}/schedules/${scheduleId}`, { method: 'DELETE' });
}

// ── Student Results API ─────────────────────────────────────

export type StudentResultRecord = {
    id: string;
    exam_schedule_id: string;
    student_id: string;
    obtained_marks: number | null;
    created_at: string;
    updated_at: string;
};

export type ResultListResponse = {
    success: boolean;
    message: string;
    data: StudentResultRecord[];
    meta: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
    };
};

export async function fetchScheduleResults(examId: string, scheduleId: string, params?: {
    perPage?: number;
}): Promise<ResultListResponse> {
    const query = new URLSearchParams();
    if (params?.perPage) query.set('per_page', String(params.perPage));
    const qs = query.toString();
    const res = await apiRequest<StudentResultRecord[]>(
        `/api/exams/${examId}/schedules/${scheduleId}/results${qs ? `?${qs}` : ''}`
    );
    return {
        success: res.success,
        message: res.message,
        data: res.data,
        meta: (res as unknown as ResultListResponse).meta,
    };
}

export async function saveStudentResult(
    examId: string,
    scheduleId: string,
    payload: { student_id: string; obtained_marks: number | null }
): Promise<StudentResultRecord> {
    const res = await apiRequest<StudentResultRecord>(
        `/api/exams/${examId}/schedules/${scheduleId}/results`,
        { method: 'POST', body: JSON.stringify(payload) }
    );
    return res.data;
}

export async function updateStudentResult(
    resultId: string,
    payload: { obtained_marks: number | null }
): Promise<StudentResultRecord> {
    const res = await apiRequest<StudentResultRecord>(
        `/api/results/${resultId}`,
        { method: 'PUT', body: JSON.stringify(payload) }
    );
    return res.data;
}

// ── Academic Sessions API ───────────────────────────────────

export type CreateAcademicSessionPayload = {
    name: string;
    start_date: string;
    end_date: string;
    is_current?: boolean;
};

export async function createAcademicSession(
    payload: CreateAcademicSessionPayload
): Promise<AcademicSessionRecord> {
    const res = await apiRequest<AcademicSessionRecord>(
        '/api/academic-sessions',
        { method: 'POST', body: JSON.stringify(payload) }
    );
    return res.data;
}

export type UpdateAcademicSessionPayload = {
    name?: string;
    start_date?: string;
    end_date?: string;
    is_current?: boolean;
};

export async function updateAcademicSession(
    sessionId: string,
    payload: UpdateAcademicSessionPayload
): Promise<AcademicSessionRecord> {
    const res = await apiRequest<AcademicSessionRecord>(
        `/api/academic-sessions/${sessionId}`,
        { method: 'PATCH', body: JSON.stringify(payload) }
    );
    return res.data;
}

export async function fetchAcademicSessions(params?: {
    perPage?: number;
}): Promise<AcademicSessionRecord[]> {
    const query = new URLSearchParams();
    if (params?.perPage) query.set('per_page', String(params.perPage));
    const qs = query.toString();
    const res = await apiRequest<AcademicSessionRecord[]>(
        `/api/academic-sessions${qs ? `?${qs}` : ''}`
    );
    return res.data;
}
